import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Drawer, IconButton, useMediaQuery } from '@mui/material';

import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { useAuth } from '../AuthContext';
import '../CSS/Menu.css';

const Menu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:1100px)');
  const [mobileOpen, setMobileOpen] = useState(false);

  const { role, logout } = useAuth();
  const roleUpper = String(role || '').toUpperCase();
  const isAdmin = roleUpper === 'ADMIN';

  const menuItems = useMemo(() => {
    const items = [
      {
        label: 'Accueil',
        path: '/',
        icon: <DashboardRoundedIcon />,
        exact: true
      },
      {
        label: 'CRA',
        path: isAdmin ? '/ListeDeclarations' : '/Déclarations',
        icon: <AssignmentTurnedInRoundedIcon />,
        match: ['/ListeDeclarations', '/Déclarations', '/Ajouterdeclarations', '/AjouterMaladie']
      },
      {
        label: 'Documents',
        path: '/Documents',
        icon: <FolderRoundedIcon />,
        match: ['/Documents']
      },
      {
        label: 'Paie',
        path: '/Paie',
        icon: <PaymentsRoundedIcon />,
        match: ['/Paie']
      }
    ];

    if (isAdmin) {
      items.splice(1, 0, {
        label: 'Ressources',
        path: '/ressources',
        icon: <GroupsRoundedIcon />,
        match: ['/ressources', '/utilisateurs']
      });

      items.push({
        label: 'Factures',
        path: '/FacturePage',
        icon: <ReceiptLongRoundedIcon />,
        match: ['/FacturePage']
      });
    }

    return items;
  }, [isAdmin]);

  const isActive = (item) => {
    const currentPath = decodeURIComponent(location.pathname || '/').toLowerCase();

    if (item.exact) return currentPath === '/';

    const paths = item.match || [item.path];

    return paths.some((path) => {
      const normalized = decodeURIComponent(path).toLowerCase();
      return currentPath === normalized || currentPath.startsWith(`${normalized}/`);
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      localStorage.removeItem('token');
      setMobileOpen(false);
      navigate('/login');
    }
  };

  const content = (
    <aside className="nova-menu">
      <div className="nova-menu-head">
        <div className="nova-menu-logo">
          <img src="/ethik-gestion-main-logo.png" alt="ETHIK GESTION" />
        </div>

        <div className="nova-menu-brand">
          <strong>ETHIK</strong>
          <span>GESTION</span>
        </div>

        {isMobile && (
          <IconButton className="nova-menu-close" onClick={() => setMobileOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        )}
      </div>
<nav className="nova-menu-nav">
        {menuItems.map((item) => (
          <NavLink
            key={item.label}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={isActive(item) ? 'nova-menu-link active' : 'nova-menu-link'}
          >
            <span className="nova-menu-icon">{item.icon}</span>
            <span className="nova-menu-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <button type="button" className="nova-menu-logout" onClick={handleLogout}>
        <LogoutRoundedIcon />
        <span>Déconnexion</span>
      </button>
    </aside>
  );

  return (
    <>
      {isMobile && (
        <header className="nova-mobile-header">
          <button type="button" className="nova-mobile-open" onClick={() => setMobileOpen(true)}>
            <MenuRoundedIcon />
            <span>Menu</span>
          </button>

          <img src="/ethik-gestion-main-logo.png" alt="ETHIK GESTION" />
        </header>
      )}

      {!isMobile && (
        <Drawer
          variant="permanent"
          open
          PaperProps={{ className: 'nova-menu-paper' }}
        >
          {content}
        </Drawer>
      )}

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ className: 'nova-menu-paper nova-menu-paper-mobile' }}
        >
          {content}
        </Drawer>
      )}
    </>
  );
};

export default Menu;
